import React, { useState, useEffect, useRef } from 'react';
import { User, Shield, Package, LogOut, FileSpreadsheet, Monitor, Bell, BarChart3 } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase, signOut } from '../lib/supabase';
import { getNotifications, markAsRead, markAllAsRead, type Notification } from '../api/notifications';
import { showPromoStripOnPath } from '../config/promo';

interface HeaderProps {
  onBackToDashboard?: () => void;
  showBackButton?: boolean;
  onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(() => {
    return localStorage.getItem('isAdmin') === 'true';
  });
  const [showProfileTip, setShowProfileTip] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [hideProfileTipFromDb, setHideProfileTipFromDb] = useState<boolean | null>(null);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifPanel(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    const load = async () => {
      const res = await getNotifications(currentUserId);
      if (res.success && res.data) setNotifications(res.data);
    };
    load();
    const channel = supabase
      .channel('user-notifications')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${currentUserId}` }, () => { load(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [currentUserId]);

  const handleMarkRead = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    await markAsRead(id);
  };

  const handleMarkAllRead = async () => {
    if (!currentUserId) return;
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    await markAllAsRead(currentUserId);
  };

  function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  }

  useEffect(() => {
    const fetchProfileTipPreference = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
        const { data } = await supabase
          .from('agents')
          .select('hide_profile_tip')
          .eq('user_id', user.id)
          .single();
        setHideProfileTipFromDb(data?.hide_profile_tip === true);
      }
    };
    fetchProfileTipPreference();
  }, []);

  useEffect(() => {
    if (hideProfileTipFromDb === null) return;
    const sessionDismissed = sessionStorage.getItem('profileTipDismissed') === 'true';
    if (sessionDismissed) return;
    let interval: NodeJS.Timeout | null = null;
    const checkProfileTip = () => {
      const welcomeModalActive = sessionStorage.getItem('welcomeModalActive') === 'true';
      const sessionDismissedNow = sessionStorage.getItem('profileTipDismissed') === 'true';
      if (!hideProfileTipFromDb && !sessionDismissedNow && !welcomeModalActive) {
        setShowProfileTip(true);
        if (interval) { clearInterval(interval); interval = null; }
      } else if ((hideProfileTipFromDb || sessionDismissedNow) && interval) {
        clearInterval(interval); interval = null;
      }
    };
    checkProfileTip();
    interval = setInterval(checkProfileTip, 500);
    return () => { if (interval) clearInterval(interval); };
  }, [hideProfileTipFromDb]);

  const location = useLocation();

  const dismissProfileTip = async () => {
    setShowProfileTip(false);
    setHideProfileTipFromDb(true);
    sessionStorage.setItem('profileTipDismissed', 'true');
    if (currentUserId) {
      try {
        await supabase.from('agents').update({ hide_profile_tip: true }).eq('user_id', currentUserId);
      } catch (error) {
        console.error('Error saving profile tip preference:', error);
      }
    }
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase.from('agents').select('role').eq('user_id', user.id).single();
        const adminStatus = data?.role === 'admin';
        setIsAdmin(adminStatus);
        localStorage.setItem('isAdmin', adminStatus ? 'true' : 'false');
      } else {
        setIsAdmin(false);
        localStorage.removeItem('isAdmin');
      }
    };
    checkAdminStatus();
  }, []);

  const handleLogout = async () => {
    try {
      if (onLogout) { onLogout(); }
      else { await signOut(); localStorage.removeItem('isAdmin'); navigate('/agent/login'); }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navItems = [
    { path: '/agent/dashboard', label: 'Lead Center', icon: Package, show: true },
    { path: '/agent/my-leads', label: 'My Leads', icon: FileSpreadsheet, show: true },
    { path: '/agent/analytics', label: 'Analytics', icon: BarChart3, show: true },
    { path: '/agent/dialer-subs', label: 'My Dialer', icon: Monitor, show: true },
    { path: '/agent/admin', label: 'Admin', icon: Shield, show: isAdmin },
    { path: '/agent/profile', label: 'Profile', icon: User, show: true },
  ].filter(item => item.show);

  // When the SMS sale strip is showing, drop the header below it (strip is h-9).
  const promoActive = showPromoStripOnPath(location.pathname);

  return (
    <header
      className={`fixed ${promoActive ? 'top-9' : 'top-0'} left-0 right-0 z-50 bg-white border-b border-slate-200`}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-14" role="navigation">
          {/* Logo */}
          <Link to="/agent/dashboard" className="flex-shrink-0">
            <img
              src="/enhance_companies_logo.png"
              alt="Enhance Companies"
              className="h-9 object-contain"
            />
          </Link>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            {navItems.map((item, index) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              const Icon = item.icon;
              return (
                <React.Fragment key={item.path}>
                  {index > 0 && <span className="text-slate-200 text-xs select-none">/</span>}
                  <Link
                    to={item.path}
                    onClick={() => item.path === '/agent/profile' && showProfileTip && dismissProfileTip()}
                    className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      isActive
                        ? 'text-slate-900 bg-slate-100'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{item.label}</span>
                    {item.badge && item.badge > 0 && (
                      <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 text-[8px] font-bold text-white bg-red-500 rounded-full">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </Link>
                </React.Fragment>
              );
            })}
          </div>

          {/* Right side: Notifications + Logout */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setShowNotifPanel(prev => !prev)}
                className="relative px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
              >
                <Bell className="w-3.5 h-3.5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 text-[8px] font-bold text-white bg-red-500 rounded-full">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifPanel && (
                <div className="absolute top-full right-0 mt-2 w-80 z-50 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-900">Notifications</p>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-[11px] font-medium text-slate-500 hover:text-slate-700 transition-colors">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-10 text-center">
                        <p className="text-xs text-slate-400">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map(n => {
                        const notifRoute = '/agent/dialer-subs';
                        return (
                          <Link
                            key={n.id}
                            to={notifRoute}
                            onClick={() => { handleMarkRead(n.id); setShowNotifPanel(false); }}
                            className={`block px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.read ? 'bg-slate-50/80' : ''}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full ${!n.read ? 'bg-slate-900' : 'bg-transparent'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-medium text-slate-900 leading-tight">{n.title}</p>
                                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{n.message}</p>
                                <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                              </div>
                              {!n.read && (
                                <button
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMarkRead(n.id); }}
                                  className="flex-shrink-0 text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                  Read
                                </button>
                              )}
                            </div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </nav>
      </div>

      {/* Profile Tip */}
      {showProfileTip && location.pathname !== '/agent/profile' && (
        <div className="absolute top-full right-20 mt-2 w-60 z-50 bg-white rounded-lg shadow-lg border border-slate-200 p-4">
          <p className="text-sm font-medium text-slate-900 mb-1">Customize Your Profile</p>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            Set your default states and add a profile picture.
          </p>
          <div className="flex items-center gap-2">
            <Link
              to="/agent/profile"
              onClick={dismissProfileTip}
              className="flex-1 text-center text-xs font-medium text-white bg-slate-900 hover:bg-slate-800 py-1.5 rounded-md transition-colors"
            >
              Go to Profile
            </Link>
            <button onClick={dismissProfileTip} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
              Dismiss
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
